/**
 * @file lib/auth/auth-middleware.ts
 * @description GFTD認証ミドルウェア
 * @author GFTD Co., Ltd.
 */

import { NextRequest, NextResponse } from 'next/server'
import { GFTDAuthClient, GFTDAuthError } from './gftd-auth-client'
import { GFTDAuthConfig } from '../types/config'

/**
 * 認証ミドルウェアのオプション
 */
export interface AuthMiddlewareOptions {
  /** 認証をスキップするパス */
  skipPaths?: string[]
  /** 認証エラー時のリダイレクトURL */
  redirectUrl?: string
  /** カスタムエラーハンドラー */
  errorHandler?: (error: GFTDAuthError, req: NextRequest) => NextResponse
}

/**
 * GFTD認証ミドルウェア
 */
export class GFTDAuthMiddleware {
  private authClient: GFTDAuthClient
  private options: AuthMiddlewareOptions

  constructor(config: GFTDAuthConfig, options: AuthMiddlewareOptions = {}) {
    this.authClient = new GFTDAuthClient(config)
    this.options = options
  }

  /**
   * ミドルウェア関数
   */
  middleware() {
    return async (req: NextRequest): Promise<NextResponse> => {
      try {
        // 認証をスキップするパスかチェック
        if (this.shouldSkipAuth(req)) {
          return NextResponse.next()
        }

        // 認証が必須でない場合はスキップ
        if (!this.authClient.isAuthRequired()) {
          return NextResponse.next()
        }

        // Authorizationヘッダーから認証情報を取得
        const authorization = req.headers.get('authorization')
        if (!authorization) {
          throw new GFTDAuthError('Authorization header missing', 401, 'AUTH_HEADER_MISSING')
        }

        // トークンを検証
        await this.validateToken(authorization)

        // 認証成功 - リクエストを続行
        return NextResponse.next()

      } catch (error) {
        if (error instanceof GFTDAuthError) {
          return this.handleAuthError(error, req)
        }
        
        console.error('Authentication middleware error:', error)
        return this.handleAuthError(
          new GFTDAuthError('Internal authentication error', 500, 'INTERNAL_ERROR'),
          req
        )
      }
    }
  }

  /**
   * API Routes用の認証チェック関数
   */
  async requireAuth(req: NextRequest): Promise<void> {
    if (!this.authClient.isAuthRequired()) {
      return
    }

    const authorization = req.headers.get('authorization')
    if (!authorization) {
      throw new GFTDAuthError('Authorization header missing', 401, 'AUTH_HEADER_MISSING')
    }

    await this.validateToken(authorization)
  }

  /**
   * トークンを検証
   */
  private async validateToken(authorization: string): Promise<void> {
    // Bearer トークンの形式をチェック
    const [type, token] = authorization.split(' ')
    if (type !== 'Bearer' || !token) {
      throw new GFTDAuthError('Invalid authorization format', 401, 'INVALID_AUTH_FORMAT')
    }

    // トークンを設定して検証
    this.authClient.setToken({
      accessToken: token,
      tokenType: 'Bearer',
      expiresAt: Date.now() / 1000 + 3600, // 仮の有効期限
      scope: []
    })

    // 実際の認証チェック
    await this.authClient.ensureAuthenticated()
  }

  /**
   * 認証をスキップするかどうか
   */
  private shouldSkipAuth(req: NextRequest): boolean {
    const pathname = req.nextUrl.pathname

    // デフォルトでスキップするパス
    const defaultSkipPaths = [
      '/api/health',
      '/api/status',
      '/favicon.ico',
      '/_next',
      '/public'
    ]

    const skipPaths = [...defaultSkipPaths, ...(this.options.skipPaths || [])]
    
    return skipPaths.some(path => pathname.startsWith(path))
  }

  /**
   * 認証エラーのハンドリング
   */
  private handleAuthError(error: GFTDAuthError, req: NextRequest): NextResponse {
    // カスタムエラーハンドラーがある場合
    if (this.options.errorHandler) {
      return this.options.errorHandler(error, req)
    }

    // デフォルトエラーハンドリング
    if (req.nextUrl.pathname.startsWith('/api/')) {
      // API ルートの場合はJSONエラーレスポンス
      return NextResponse.json(
        {
          error: error.message,
          code: error.code,
          statusCode: error.statusCode
        },
        { status: error.statusCode }
      )
    } else {
      // 通常のページの場合はログインページにリダイレクト
      const loginUrl = this.options.redirectUrl || '/login'
      const url = req.nextUrl.clone()
      url.pathname = loginUrl
      url.searchParams.set('redirect', req.nextUrl.pathname)
      
      return NextResponse.redirect(url)
    }
  }

  /**
   * 認証クライアントを取得
   */
  getAuthClient(): GFTDAuthClient {
    return this.authClient
  }
}

/**
 * 認証ミドルウェアファクトリー関数
 */
export function createAuthMiddleware(
  config: GFTDAuthConfig, 
  options?: AuthMiddlewareOptions
): GFTDAuthMiddleware {
  return new GFTDAuthMiddleware(config, options)
}

/**
 * React Hook用の認証チェック
 */
export function useAuth(authClient: GFTDAuthClient) {
  const checkAuth = async (): Promise<boolean> => {
    try {
      await authClient.ensureAuthenticated()
      return true
    } catch (error) {
      console.error('Authentication check failed:', error)
      return false
    }
  }

  const login = (redirectUrl?: string): string => {
    return authClient.generateLoginUrl(redirectUrl)
  }

  const logout = async (): Promise<void> => {
    await authClient.logout()
  }

  const getCurrentUser = () => {
    return authClient.getCurrentUser()
  }

  const isAuthenticated = (): boolean => {
    return authClient.isAuthenticated()
  }

  return {
    checkAuth,
    login,
    logout,
    getCurrentUser,
    isAuthenticated,
    authRequired: authClient.isAuthRequired()
  }
} 